import React from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import AboutHero from './AboutHero';
import './WarrantyPolicy.css';

const WarrantyPolicy = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;

  const handleOpenRma = (e) => {
    e.preventDefault();
    console.log('Dispatching open_rma_modal event from WarrantyPolicy page.');
    window.dispatchEvent(new Event('open_rma_modal'));
  };

  return (
    <div className="warranty-page-wrapper">
      <AboutHero />
      
      <div className="warranty-content-container">
        
        {/* Intro Section */}
        <div className="warranty-intro-card animate-fade-in">
          <div className="warranty-intro-icon">🛡️</div>
          <h2>Warranty Policy - RMA</h2>
          <p>
            <strong>Recent Inspiration</strong> offers a <strong>2-year warranty</strong> for all its products and representations, from the moment of purchase by the end consumer.
          </p>
          <p>
            The start of the warranty is documented by presenting the retail receipt to the store from which the product was purchased, as well as by registering an RMA number. Without a product return form and relevant shipping note, we will not receive the products, and they will be returned with a transportation charge to the recipient.
          </p>
        </div>

        {/* Two-Column Grid: Terms & Cancellations */}
        <div className="warranty-grid">
          
          {/* Section 1: Warranty Terms */}
          <div className="warranty-card animate-fade-in">
            <h3 className="card-heading">1. Warranty Terms</h3>
            <ul className="warranty-list">
              <li>
                <span className="list-bullet">✓</span>
                <span className="list-text">The warranty period is valid from the date of purchase by the first consumer and is transferable only between end consumers.</span>
              </li>
              <li>
                <span className="list-bullet">✓</span>
                <span className="list-text">Only manufacturing defects are covered.</span>
              </li>
              <li>
                <span className="list-bullet">✓</span>
                <span className="list-text">Recent Inspiration’s liability is limited to the repair and/or replacement of the product in accordance with the warranty.</span>
              </li>
              <li>
                <span className="list-bullet">✓</span>
                <span className="list-text">Products must be returned in their original packaging with all their accessories. Lack of packaging or accessories results in the replacement being made only on the contents of the products upon receipt, and credit cannot be given to an incomplete product.</span>
              </li>
              <li>
                <span className="list-bullet">✓</span>
                <span className="list-text">The batteries of the products are covered by a <strong>6-month warranty</strong>.</span>
              </li>
            </ul>
          </div>

          {/* Section 3: Cancellations */}
          <div className="warranty-card animate-fade-in">
            <h3 className="card-heading text-red">3. Warranty Cancellations</h3>
            <p className="section-intro-text">The warranty is canceled in case of:</p>
            <ul className="warranty-list cancellations-list">
              <li>
                <span className="list-bullet">✗</span>
                <span className="list-text">Misuse or negligence.</span>
              </li>
              <li>
                <span className="list-bullet">✗</span>
                <span className="list-text">Violation or incorrect adjustment of the product.</span>
              </li>
              <li>
                <span className="list-bullet">✗</span>
                <span className="list-text">Changes or repairs by unauthorized persons.</span>
              </li>
            </ul>

            <div className="doa-notice-box">
              <h4>DOA (Dead on Arrival) Cases:</h4>
              <p>Products that do not work upon receipt (DOA) are covered within <strong>7 days</strong> of receipt.</p>
            </div>
          </div>

        </div>

        {/* Section 2: Zero Pixel Warranty */}
        <div className="warranty-zero-pixel-card animate-fade-in">
          <div className="zero-pixel-badge">ZERO PIXEL GUARANTEE</div>
          <div className="zero-pixel-content">
            <h3>2. 30-Day Zero Pixel Warranty</h3>
            <p>
              We stand behind the quality of our displays with a <strong>30-Day Zero Pixel Warranty</strong>. If your screen develops even a single bright or dead pixel within 30 days of purchase, we will replace it free of charge, subject to our warranty terms and inspection process. This warranty ensures you receive a flawless viewing experience with complete peace of mind.
            </p>
          </div>
        </div>

        {/* Out of Warranty Cases Section */}
        <div className="warranty-out-section animate-fade-in">
          <h3 className="section-title">Out of Warranty Cases</h3>
          <p className="section-subtitle">For categories: Tablets, Mobile Phones, Laptops, and similar devices:</p>
          
          <div className="out-warranty-grid">
            <div className="out-case-card">
              <div className="case-num">04</div>
              <h4>Screen Damage</h4>
              <p>Broken, cracked, or scratched screen.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">05</div>
              <h4>Interface Damage</h4>
              <p>Damaged charging port or part of each interface, including other ports.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">06</div>
              <h4>Metal Filings</h4>
              <p>Metal filings on the speaker, magnetic parts, or inside the device.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">07</div>
              <h4>Liquid & Corrosives</h4>
              <p>Paint, dust, chemicals (e.g. strong corrosives or alcohol), and all liquids.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">08</div>
              <h4>Unauthorized Disassembly</h4>
              <p>Unauthorized disassembly or opening of the device.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">09</div>
              <h4>Casing Malfunction</h4>
              <p>Damage to the casing where it is the cause of device malfunction.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">10</div>
              <h4>Third-Party Chargers</h4>
              <p>Unauthorized use of a third-party charging device.</p>
            </div>
            <div className="out-case-card">
              <div className="case-num">11</div>
              <h4>Natural Disasters & Force</h4>
              <p>Natural disasters, extreme low/high temperatures, radiation, drops/falls, or pressure exceeding limits.</p>
            </div>
          </div>

          <div className="warranty-footnotes">
            <p>Do not exclude any device even with special resistance specifications. Crystal screens are not covered by breakage after bad or improper use.</p>
            <p>Excluding devices where a high level of resistance is provided based on specifications up to the point and conditions specified by the manufacturer.</p>
            <p>Excluding Waterproof devices depending on their exposure time to the liquid element, or to temperatures, or to pressures within the maximum permitted limits (e.g., IP54-IP69), as provided by the manufacturer.</p>
          </div>
        </div>

        {/* Contact/Submit Block */}
        <div className="warranty-action-card animate-fade-in">
          <h3>Need to submit an RMA claim?</h3>
          <p>
            The RMA form must be completed and sent to our support emails:
          </p>
          <div className="email-links-row">
            <a href="mailto:smarttelecom.nl@gmail.com" className="email-badge">smarttelecom.nl@gmail.com</a>
            <a href="mailto:info@smarttelecomstore.com" className="email-badge">info@smarttelecomstore.com</a>
          </div>
          <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: '#64748b' }}>
            You can also submit an RMA request directly from your online account panel.
          </p>
          <button className="open-rma-btn" onClick={handleOpenRma}>
            Submit RMA Form Online
          </button>
        </div>

      </div>
    </div>
  );
};

export default WarrantyPolicy;
